import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Mail, Phone, Building2, Calendar, MapPin, Pencil, Trash2, FileText, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Avatar, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { money, dateStr } from "@/lib/format";
import api from "@/lib/api";
import { toast } from "sonner";
import { locationOptions } from "@/lib/locations";


export default function Profile() {
  const { user, employee, logout, loadUser } = useAuth();
  const navigate = useNavigate();
  const roleLabel = { admin: "Admin", admin_staff: "Admin + Staff", team_leader: "Team Leader", staff: "Staff" }[user.role];
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">My Profile</h1>
        <Button onClick={() => setShowEdit(true)} variant="outline" className="rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50 gap-2">
          <Pencil className="w-4 h-4" /> Edit Profile
        </Button>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 p-6">
        <div className="flex items-center gap-4">
          <Avatar src={employee?.photo} name={user.name} size={72} />
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-heading text-xl font-bold text-slate-900">{user.name}</h2>
              <StatusBadge status={roleLabel} />
            </div>
            <p className="text-slate-500 text-sm">{employee?.designation} {employee?.department && `· ${employee.department}`}</p>
            {employee && <p className="text-xs text-slate-400 mt-0.5">{employee.employee_code}</p>}
          </div>
        </div>

        {employee && (
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 mt-6 text-sm">
            <Info icon={Mail} text={user.email} />
            <Info icon={Phone} text={employee.phone} />
            <Info icon={Building2} text={employee.department} />
            <Info icon={MapPin} text={employee.location} />
            <Info icon={Calendar} text={`Joined ${dateStr(employee.joining_date)}`} />
          </div>
        )}
      </div>

      {employee && (
        <div className="rounded-2xl bg-white border border-slate-200 p-6">
          <p className="text-sm text-slate-400">Monthly Salary</p>
          <p className="font-heading text-2xl font-bold text-slate-900 mt-1">{money(employee.monthly_salary)}</p>
          <p className="text-xs text-slate-400 mt-0.5">{employee.salary_type}</p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={() => { logout(); navigate("/login"); }} variant="outline" className="rounded-xl border-red-200 text-red-600 hover:bg-red-50" data-testid="profile-logout">
          <LogOut className="w-4 h-4 mr-2" /> Logout
        </Button>
        <Button onClick={() => setShowDelete(true)} variant="outline" className="rounded-xl border-red-200 text-red-600 hover:bg-red-50" data-testid="profile-delete-account">
          <Trash2 className="w-4 h-4 mr-2" /> Delete My Account
        </Button>
        <Button onClick={() => navigate("/privacy-policy")} variant="outline" className="rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50" data-testid="profile-privacy">
          <ShieldCheck className="w-4 h-4 mr-2" /> Privacy Policy
        </Button>
        <Button onClick={() => navigate("/terms-and-conditions")} variant="outline" className="rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50" data-testid="profile-terms">
          <FileText className="w-4 h-4 mr-2" /> Terms and Conditions
        </Button>
      </div>

      {showDelete && (
        <DeleteAccountModal
          onClose={() => setShowDelete(false)}
          onDeleted={() => { logout(); navigate("/login"); }}
        />
      )}
      
      {showEdit && (
        <EditProfileModal
          user={user}
          employee={employee}
          onClose={() => setShowEdit(false)}
          onSuccess={() => {
            setShowEdit(false);
            loadUser(); // refresh user state globally
          }}
        />
      )}
    </div>
  );
}

const Info = ({ icon: Icon, text }) => (
  <div className="flex items-center gap-2 text-slate-600"><Icon className="w-4 h-4 text-slate-400" /> {text || "—"}</div>
);

function DeleteAccountModal({ onClose, onDeleted }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/users/me/delete", { password });
      toast.success("Your account has been deleted");
      onDeleted();
      return;
    } catch (err) {
      setError(err.response?.data?.detail || "Could not delete account. Please try again.");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl animate-in zoom-in-95 my-8">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-red-600">Delete My Account</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">&times;</button>
        </div>
        <form onSubmit={onSubmit} className="p-6 space-y-4">
          <p className="text-sm text-slate-600">
            This permanently deletes your login, profile, attendance, leave and payroll records. This cannot be undone.
          </p>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase">Enter your password to confirm</label>
            <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-red-400 transition-colors" />
          </div>
          {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>}
          <div className="pt-2 flex justify-end gap-3">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy || !password} className="bg-red-500 hover:bg-red-600 text-white">
              {busy ? "Deleting..." : "Delete permanently"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditProfileModal({ user, employee, onClose, onSuccess }) {
  const [data, setData] = useState({ 
    name: user.name || "", 
    email: user.email || "", 
    password: "",
    phone: employee?.phone || "",
    department: employee?.department || "",
    designation: employee?.designation || "",
    location: employee?.location || "",
    joining_date: employee?.joining_date || "",
    salary_type: employee?.salary_type || "Monthly"
  });
  const [busy, setBusy] = useState(false);
  const [locs, setLocs] = useState([]);
  const canEditJoin = user.role === "admin" || user.role === "admin_staff";
  useEffect(() => { api.get("/locations").then((r) => setLocs(r.data)).catch(() => {}); }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...data };
      if (!data.password) delete payload.password;
      if (!canEditJoin) delete payload.joining_date;
      await api.put("/users/me", payload);
      toast.success("Profile updated successfully");
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl animate-in zoom-in-95 my-8">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl z-10">
          <h2 className="font-heading text-lg font-bold">Edit Profile</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">&times;</button>
        </div>
        <form onSubmit={onSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase">Full Name</label>
              <input required type="text" value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-emerald-500 transition-colors" />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase">Email Address</label>
              <input required type="email" value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-emerald-500 transition-colors" />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase">Phone Number</label>
              <input type="text" value={data.phone} onChange={(e) => setData({ ...data, phone: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-emerald-500 transition-colors" />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase">Location</label>
              <select value={data.location} onChange={(e) => setData({ ...data, location: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-emerald-500 transition-colors">
                <option value="">Select</option>
                {locationOptions(locs, data.location).map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase">Department</label>
              <input type="text" value={data.department} onChange={(e) => setData({ ...data, department: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-emerald-500 transition-colors" />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase">Designation</label>
              <input type="text" value={data.designation} onChange={(e) => setData({ ...data, designation: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-emerald-500 transition-colors" />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase">Joining Date</label>
              <input type="date" value={data.joining_date} disabled={!canEditJoin} onChange={(e) => setData({ ...data, joining_date: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-emerald-500 transition-colors disabled:opacity-60 disabled:cursor-not-allowed" />
              {!canEditJoin && <p className="text-[11px] text-slate-400 mt-1">Only Admin can change this</p>}
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase">Salary Type</label>
              <select value={data.salary_type} onChange={(e) => setData({ ...data, salary_type: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-emerald-500 transition-colors">
                <option value="Monthly">Monthly</option>
                <option value="Hourly">Hourly</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase">New Password</label>
              <input type="password" value={data.password} onChange={(e) => setData({ ...data, password: e.target.value })} placeholder="Leave blank to keep current" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-emerald-500 transition-colors" />
            </div>
          </div>
          <div className="pt-4 flex justify-end gap-3 border-t border-slate-100 mt-6">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? "Saving..." : "Save Changes"}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

